import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Eye, CheckCircle2, Ban, Loader2, Building2, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiErr } from "@/lib/api";
import { StatusBadge } from "./AdminOverview";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [usage, setUsage] = useState(null);
  const [calls, setCalls] = useState(null);
  const [leads, setLeads] = useState(null);

  const load = () => adminApi.get(`/admin/clients/${id}`).then((r) => setC(r.data)).catch(() => {});
  useEffect(() => {
    load();
    adminApi.get(`/admin/clients/${id}/usage`).then((r) => setUsage(r.data)).catch(() => {});
    adminApi.get(`/admin/clients/${id}/calls`).then((r) => setCalls(r.data)).catch(() => {});
    adminApi.get(`/admin/clients/${id}/leads`).then((r) => setLeads(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  const act = async (action, label) => { try { await adminApi.post(`/admin/clients/${id}/${action}`); toast.success(label); load(); } catch (e) { toast.error(apiErr(e)); } };
  const impersonate = async () => {
    try { await adminApi.post(`/admin/clients/${id}/impersonate`); toast.success("Opening client panel..."); setTimeout(() => { window.location.href = "/"; }, 500); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!c) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "#7C3AED" }} /></div>;

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/admin/clients")} className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-800 dark:hover:text-white" data-testid="admin-back-clients"><ArrowLeft className="w-4 h-4" /> Clients</button>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: "linear-gradient(135deg,#7C3AED,#06B6D4)" }}>{c.company_name?.slice(0, 1)}</div>
          <div>
            <h1 className="text-2xl font-bold font-display flex items-center gap-2">{c.company_name} <StatusBadge status={c.status} /></h1>
            <p className="text-sm text-slate-500">{c.plan} · {fmtINR(c.plan_price_inr)}/mo · renews {(c.plan_renewal_at || "—").slice(0, 10)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {c.status === "pending" && <Button variant="outline" className="text-emerald-600" onClick={() => act("approve", "Approved")} data-testid="detail-approve"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>}
          {c.status === "active" && <Button variant="outline" className="text-red-600" onClick={() => act("suspend", "Suspended")} data-testid="detail-suspend"><Ban className="w-4 h-4 mr-1" />Suspend</Button>}
          {c.status === "suspended" && <Button variant="outline" className="text-emerald-600" onClick={() => act("activate", "Activated")} data-testid="detail-activate"><CheckCircle2 className="w-4 h-4 mr-1" />Activate</Button>}
          <Button style={{ background: "#7C3AED" }} onClick={impersonate} data-testid="detail-impersonate"><Eye className="w-4 h-4 mr-1" />Login as Client</Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList><TabsTrigger value="overview" data-testid="detail-tab-overview">Overview</TabsTrigger><TabsTrigger value="usage" data-testid="detail-tab-usage">Usage</TabsTrigger><TabsTrigger value="calls" data-testid="detail-tab-calls">Calls</TabsTrigger><TabsTrigger value="leads" data-testid="detail-tab-leads">Leads</TabsTrigger></TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border shadow-none p-6 space-y-3">
              <h3 className="font-semibold font-display text-sm">Company</h3>
              <Row icon={Building2} label="Company" value={c.company_name} />
              <Row icon={Mail} label="Contact" value={`${c.contact_name || "—"} · ${c.contact_email}`} />
              <Row icon={Phone} label="Mobile" value={c.contact_mobile || "—"} />
              <Row icon={MapPin} label="Address" value={c.address || "—"} />
              <Row label="GST" value={c.gst_number || "—"} />
            </Card>
            <Card className="border shadow-none p-6 space-y-3">
              <h3 className="font-semibold font-display text-sm">Integrations</h3>
              <div className="grid grid-cols-2 gap-2">
                {c.integrations.map((it) => (
                  <div key={it.type} className="flex items-center justify-between text-sm border rounded-lg px-3 py-1.5">
                    <span className="capitalize">{it.type.replace(/_/g, " ")}</span>
                    <span className={`w-2 h-2 rounded-full ${it.status === "connected" ? "bg-emerald-500" : "bg-slate-300"}`} />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="usage">
          {!usage ? <Loader2 className="w-5 h-5 animate-spin" /> :
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Total Calls" value={usage.calls} /><Stat label="Leads" value={usage.leads} /><Stat label="Inbound" value={usage.inbound} /><Stat label="Outbound" value={usage.outbound} /><Stat label="Hot Leads" value={usage.hot} /><Stat label="Qualified" value={usage.qualified} />
              </div>
              <Card className="border shadow-none p-5"><h3 className="font-semibold font-display text-sm mb-4">Daily Calls (7 days)</h3>
                <ResponsiveContainer width="100%" height={220}><BarChart data={usage.daily}><CartesianGrid strokeDasharray="3 3" opacity={0.15} vertical={false} /><XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="calls" fill="#7C3AED" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
              </Card>
            </div>}
        </TabsContent>

        <TabsContent value="calls">
          <Card className="border shadow-none">
            {!calls ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
              calls.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No calls.</div> :
                <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Lead</th><th className="px-4 py-2">Direction</th><th className="px-4 py-2">Outcome</th><th className="px-4 py-2">Score</th><th className="px-4 py-2">When</th></tr></thead>
                  <tbody>{calls.map((cl) => <tr key={cl.id} className="border-b"><td className="px-4 py-2.5 font-medium">{cl.lead_name}</td><td className="px-4 py-2.5 capitalize">{cl.direction}</td><td className="px-4 py-2.5">{cl.outcome}</td><td className="px-4 py-2.5">{cl.score}</td><td className="px-4 py-2.5 text-slate-400">{(cl.started_at || "").slice(0, 16).replace("T", " ")}</td></tr>)}</tbody></table>}
          </Card>
        </TabsContent>

        <TabsContent value="leads">
          <Card className="border shadow-none">
            {!leads ? <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
              leads.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No leads.</div> :
                <table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Name</th><th className="px-4 py-2">Mobile</th><th className="px-4 py-2">City</th><th className="px-4 py-2">Interest</th><th className="px-4 py-2">Score</th><th className="px-4 py-2">Status</th></tr></thead>
                  <tbody>{leads.map((l) => <tr key={l.id} className="border-b"><td className="px-4 py-2.5 font-medium">{l.name}</td><td className="px-4 py-2.5">{l.mobile}</td><td className="px-4 py-2.5">{l.city}</td><td className="px-4 py-2.5">{l.interest}</td><td className="px-4 py-2.5">{l.score}</td><td className="px-4 py-2.5">{l.status}</td></tr>)}</tbody></table>}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return <div className="flex items-center gap-2 text-sm">{Icon && <Icon className="w-4 h-4 text-slate-400" />}<span className="text-slate-400 w-20">{label}</span><span className="font-medium">{value}</span></div>;
}
function Stat({ label, value }) {
  return <Card className="border shadow-none p-4"><div className="text-xs text-slate-500">{label}</div><div className="text-2xl font-bold font-display mt-1">{value}</div></Card>;
}
